/**
 * Class for text elements
 */
window.ShapedTextElement = class extends qtee.customDesign.AbstractElement {

	constructor (model, paper, opts) {

		// create Raphael Set (for text and rect) and call AbstractElement constructor on it
		super(paper.set(), opts);

		this.loadFromModel(model);

		// since Raphael js does note assign ID for sets we create one for this element
		// just Date.now() or Math.random() is not enough
		this.rel.id = this.rel.id ? this.rel.id : `lp${Date.now()}${Math.random()}`;

		// we use it like internal constants
		this._stretch = false;
		this._letterSpacing = 0; // float in range `-1..1`, default is `0`
		this._lineSpacing = 1; // float in range `1..3`, default is `1`
		this._stroke = 'none';

		// to increase UI performance we create queue of text changes
		this._textChanges = [];

		// since order of rect and text in the set may be changed so it's worth to use named alias instead of array index
		this._el = {
			text: null,
			rect: null
		};

		// concurency control
		this._ci = 0;

	}

	/**
	 * Return promise that resolved when font file is downloaded
	 * @protected
	 * @param {String} fontName
	 * @param {Integer} weight
	 * @param {'italic'|'normal'} style
	 * @param {String} stretch
	 * @return {Promise} resolved when font file is downloaded
	 */
	_fontReady (fontName = this._fontFamily, weight = this._weight, style = this._style, stretch = this._stretch) {

		return qtee.customDesign.fonts.addFont(fontName, weight, style);

	}

	/**
	 * This method is actually print text, we have to use it only once then we transform element instead of recreate it
	 * @protected
	 * @param {Cufon.Font} font
	 * @return {this}
	 */
	_create (font) {

		let bbox;
		let dx;
		let dy;

		// create Raphael Text Element like combined paths
		const text = this.rel.paper.pathPrint(
			this._origin.x,
			this._origin.y,
			this._text,
			font,
			this._fontSize,
			this._letterSpacing, // letter spacing
			this._lineSpacing // line spacing
		);

		// add to canvas
		this._el.text = this.rel.paper.path(text.path).attr({ fill: this._fill, stroke: this._stroke });

		// create rect
		this._el.rect = this.rel.paper.rect(text.x, text.y, text.width, text.height);

		// transform rect to fit text and style it
		this._el.rect.transform(this._el.text.transform());
		this._el.rect.attr({ fill: 'rgba(255, 255, 250, 0)', stroke: 'none' });
		this.rel.push(this._el.text, this._el.rect);

		// HACK: we redefine default Raphael's getBBox function for the set
		this.rel.getBBox = (b) => this._el.rect.getBBox(b);

		// add Free Transform mixin to element ( the Raphael Set )
		this.rel.paper.freeTransform(this.rel, this._opts.freeTransform, this._opts.callback);

		// rotate
		this.setR(this._origin.rotation).apply();

		return this;

	}

	/**
	 * Rerender text
	 * @protected
	 */
	_reRenderText () {

		let newText;
		let bbox;
		const font = qtee.customDesign.fonts.getFont(this._fontFamily, this._weight, this._style);

		// if font is not downloaded yet then skip this rendering
		if (font) {

			// calculate Raphael Text Element like combined paths
			newText = this.rel.paper.pathPrint(
				this._origin.x,
				this._origin.y,
				this._text,
				font,
				this._fontSize,
				this._letterSpacing,
				this._lineSpacing
			);

			// if newText is empty (spaces, tabs etc) and element is not hidden then hide it
			if (newText.path.length == 0) {

				newText = this.rel.paper.pathPrint(
					this._origin.x,
					this._origin.y,
					'x',
					font,
					this._fontSize,
					this._letterSpacing,
					this._lineSpacing
				);

				if (this._el.text.attr('fill-opacity') != 0) {

					// change element and handles' opacities to zero for empty text element
					this._el.text.attr({ 'fill-opacity': 0 });
					this._oldAttrs = {
						'fill-opacity': this._opts.freeTransform.attrs['fill-opacity'],
						stroke: this._opts.freeTransform.attrs.stroke
					};
					this._opts.freeTransform.attrs['fill-opacity'] = 0;
					this._opts.freeTransform.attrs.stroke = 'rgba(255, 255, 255, 0)';

				}

			} else if (this._el.text.attr('fill-opacity') == 0) {

				// restore previous values since text element was empty and it's not empty anymore
				this._el.text.attr({ 'fill-opacity': 1 });
				this._opts.freeTransform.attrs['fill-opacity'] = this._oldAttrs['fill-opacity'];
				this._opts.freeTransform.attrs.stroke = this._oldAttrs.stroke;

			}

			// replace text
			this._el.text.attr('path', newText.path);

			// change rectangle to fit text's bounding box
			bbox = Raphael.pathBBox(newText);
			this._el.rect.attr({
				x: newText.x,
				y: newText.y,
				height: newText.height,
				width: newText.width
			});

		}

		return this;

	}

	/**
	 * Obtain font based on props and then redraw element
	 * @protected
	 * @param {Object} props
	 * @return {Promise} resolved when text with new properties are rerendred
	 */
	_setProperties (props) {

		const oldProps = {};
		let prop;
		let ci;

		for (prop in props) {

			oldProps[prop] = this[prop];
			this[prop] = props[prop];

		}

		// concurency control
		this._ci = ++this._ci || 0;
		ci = this._ci;

		return this._fontReady().then(
			(font) => {

				// concurency control
				if (ci < this._ci) {

					return Promise.reject('New changes were requested by user');

				}

				const r = this._reRenderText().resetBoundingBox();

				// to keep center inside borders
				this.rel.freeTransform.applyLimits().apply();

				return r;

			},
			(reason) => {

				// restore values if promise has been rejected
				for (prop in oldProps) {

					this[prop] = oldProps[prop];

				}

				return Promise.reject(reason);

			}
		);

	}

	/**
	 * @return {Promise} resolved when element is created
	 */
	ready () {

		// check if element is already ready
		if (this.rel.length > 0) {

			return Promise.resolve(this);

		}

		return this._fontReady().then(
			(font) => {

				this._create(font);
				return this; // return this for chaining promises

			},
			(reason) => {

				throw reason;

			}
		);

	}

	/**
	 * Update text and redraw it
	 * @return {this}
	 */
	setText (text) {

		this._textChanges.push(text);

		return new Promise((resolve, reject) => {

			setTimeout(() => {

				if (!this._textChanges.length) {

					reject('No need to render old text');

				} else {

					this._text = this._textChanges.pop();
					this._textChanges = [];

					this._reRenderText().resetBoundingBox();

					// to keep center inside borders
					this.rel.freeTransform.applyLimits().apply();

					resolve(this);

				}

			}, 0);

		});

	}

	getText () {

		return this._text;

	}

	isEmpty () {

		return this._text.trim().length == 0;

	}

	/**
	 * Change font family, weight and type
	 * @return {Promise}
	 */
	setFont (fontFamily, weight, type) {

		return this._setProperties({
			_fontFamily: fontFamily,
			_weight: weight,
			_style: type
		});

	}

	getFontFamily () {

		return this._fontFamily;

	}

	setFontSize (fontSize) {

		this._fontSize = fontSize;
		this._reRenderText().resetBoundingBox();

		// to keep center inside borders
		this.rel.freeTransform.applyLimits().apply();

		return Promise.resolve(this);

	}

	getFontSize () {

		return this._fontSize;

	}

	/** @param {Bool} b */
	setBold (b) {

		return this._setProperties({
			_weight: b ? qtee.customDesign.fonts.weights.bold : qtee.customDesign.fonts.weights.regular
		});

	}

	makeBold () {

		return this.setBold(true);

	}

	makeUnBold () {

		return this.setBold(false);

	}

	isBold () {

		return this._weight == qtee.customDesign.fonts.weights.bold;

	}

	makeRegular () {

		return this._setProperties({ _weight: qtee.customDesign.fonts.weights.regular, _style: 'normal' });

	}

	/**
	 * @param {Bool} b
	 * @return {Promise}
	 */
	setItalic (b) {

		return this._setProperties({ _style: b ? 'italic' : 'normal' });

	}

	makeItalic () {

		return this.setItalic(true);

	}

	makeUnItalic () {

		return this.setItalic(false);

	}

	isItalic () {

		return this._style == 'italic';

	}

	isRegular () {

		return !this.isBold() && !this.isItalic();

	}

	getFill () {

		return this._el.text.attr('fill');

	}

	setFill (newFill) {

		this._el.text.attr({ fill: newFill });
		this._fill = newFill;
		return Promise.resolve(this);

	}

	saveToModel () {

		const rgb = this._el.text.attr('fill').match(/\d+/g);

		return {
			OriginX: this.getX(),
			OriginY: this.getY(),
			RotationalDegree: this.getR(),
			Text: this._text,
			Red: parseInt(rgb[0]),
			Green: parseInt(rgb[1]),
			Blue: parseInt(rgb[2]),
			FontName: qtee.customDesign.fonts.getFontName(this._fontFamily, this._weight),
			FontSize: this._fontSize * this.getS(),
			IsBold: false, // yeah, we don't use this field anymore since font family can has more than only one weight
			IsItalic: this._style == 'italic',
			ZOrder: this.zIndex
		};

	}

	loadFromModel (model) {

		// we have to save some of original values
		this._origin = Object.freeze({
			x: model.get('OriginX'),
			y: model.get('OriginY'),
			rotation: model.get('RotationalDegree')
		});

		// since we use path instead of text svg element we need to save data about text
		this._fill = `rgb(${model.get('Red')},${model.get('Green')},${model.get('Blue')})`;
		this._text = model.get('Text');
		this._fontSize = model.get('FontSize');
		this._style = model.get('IsItalic') ? 'italic' : 'normal';

		const font = qtee.customDesign.fonts.parseFontName(model.get('FontName'));
		this._fontFamily = font.family;
		this._weight = font.weight;

		this._sid = model.id;
		this._cid = model.cid;
		this._type = model.type;
		this.zIndex = model.get('ZOrder');

	}

};
